import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

// Asks the native side for a receipt photo and hands it to the voice modal,
// which sends it down the same road a spoken sentence takes.
//
// Nothing is uploaded from native. The image comes back as a downsampled JPEG
// data URL and the request to Rails is made here in the webview, so the session
// cookie and the CSRF token keep working exactly as they already do — the same
// division of labour bridge--plaid-link uses.
//
// The buttons are inert in a plain browser: `this.enabled` is false when no
// native component is connected, so a desktop visitor sees the Scan panel and
// simply cannot start a capture. That is deliberate; there is no web camera
// fallback and a half-working one would be worse than none.
export default class extends BridgeComponent {
  static component = "expense-scan"
  static targets = ["button"]

  // A capture must not outlive the picker. Native replies on every path it
  // knows about, but a reply that fails to deliver is swallowed there
  // (ExpenseScanComponent#finish logs it and moves on), and without a watchdog
  // the buttons would stay disabled for the life of the page — this controller
  // stays connected as long as the dashboard does, so closing the sheet does
  // not clear it. Two minutes is longer than anyone spends in a photo library
  // and far short of leaving the Scan panel permanently dead.
  static CAPTURE_TIMEOUT_MS = 120000

  disconnect() {
    super.disconnect()
    clearTimeout(this._captureTimeout)
  }

  // data-action="click->bridge--expense-scan#capture" with data-source on the
  // button. `source` decides which permission is asked for, and asking happens
  // natively at the tap — declining the camera says nothing about the library,
  // and neither says anything about the microphone.
  capture(event) {
    event.preventDefault()
    if (!this.enabled) return
    if (this._busy) return

    const source = event.currentTarget.dataset.source === "camera" ? "camera" : "library"

    console.log("[Scan] capture requested:", source)
    this.#setBusy(true)
    this.send("capture", { source }, (message) => this.#handleResult(message))
  }

  // ── Private ────────────────────────────────────────────────────────────────

  #handleResult(message) {
    console.log("[Scan] reply:", message.event, "bytes:", (message.data?.imageDataUrl || "").length)
    this.#setBusy(false)

    const modal = this.#voiceModal
    if (!modal) {
      console.warn("[Scan] no voice-modal controller found")
      return
    }

    switch (message.event) {
      case "captured":
        modal.scanImage(message.data?.imageDataUrl)
        break
      case "denied":
        modal.scanFailed(this.#message("camera-denied"))
        break
      case "unavailable":
        modal.scanFailed(this.#message("failed"))
        break
      // "cancelled" is not a failure. The user changed their mind; say nothing.
    }
  }

  // Guards against a second tap while the picker is opening. Native drops a
  // duplicate capture without replying, which would otherwise strand the
  // buttons disabled.
  //
  // The timer is the recovery for the reply that never comes: it hands the
  // buttons back and tells the sheet, rather than leaving a panel that looks
  // fine and answers nothing.
  #setBusy(busy) {
    this._busy = busy
    clearTimeout(this._captureTimeout)

    if (busy) {
      this._captureTimeout = setTimeout(() => {
        console.warn("[Scan] no reply within timeout, releasing buttons")
        this.#setBusy(false)
        this.#voiceModal?.scanFailed(this.#message("failed"))
      }, this.constructor.CAPTURE_TIMEOUT_MS)
    }

    this.buttonTargets.forEach((button) => {
      button.disabled = busy
      button.classList.toggle("opacity-60", busy)
    })
  }

  #message(key) {
    return this.bridgeElement.bridgeAttribute(`${key}-message`) || ""
  }

  get #voiceModal() {
    const el = this.element.closest("[data-controller~='voice-modal']")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "voice-modal")
  }
}
